<template>
  <div class="superset-wrapper">
    <div v-if="loading" class="superset-loading">
      <span>Loading dashboard…</span>
    </div>
    <div v-if="error" class="superset-error">{{ error }}</div>
    <!-- The SDK mounts an <iframe> inside this div -->
    <div ref="mount" class="superset-mount" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
const SUPERSET_DOMAIN = process.env.VUE_APP_SUPERSET_URL;

export default {
  name: "SupersetDashboard",

  data() {
    return {
      loading: false,
      error: null,
      DASHBOARD_UUID: null,
      EMBEDDED_ID: null,
      iframeSrc: null,
    };
  },



  async mounted() {
    if(this.isEducationalInstituteOrg){
      this.DASHBOARD_UUID = "f375e0b0-4cff-4567-9171-60e0b186c6a6";
      this.EMBEDDED_ID = "857b90b9-2386-46e9-b011-92e2cf37d22f";
    }else {
      this.DASHBOARD_UUID = "666c9ccd-15fc-4cd7-b138-696f904d9478";
      this.EMBEDDED_ID = "d51b8405-3e6a-48af-8c6c-4512b5bbe847";
    }
    await this.initEmbed();
  },

  computed: {
    ...mapGetters(["getCustomerId", "getOrgIDFromAccessToken","isEducationalInstituteOrg","getIsParentUserFromAccessToken"]),
  },

  methods: {
    ...mapActions(["fetchSupersetGuestToken"]),
    async fetchGuestToken() {
      const customerId = this.getCustomerId;
      const orgId = this.getOrgIDFromAccessToken;
      let rlsRules = [
        {
          clause: `customer_uid = '${customerId}'`,
        },
      ];

      if(!this.getIsParentUserFromAccessToken){
        rlsRules = [
          {
            clause: `customer_uid = '${customerId}' AND organisation_id = '${orgId}'`,
          },
        ];
      }

      const payload = {
        username: "admin",
        password: "admin",
        dashboard_id: this.EMBEDDED_ID,
        rls: rlsRules,
      };
      const resp = await this.fetchSupersetGuestToken(payload);
      const { guest_token } = resp.data;
      return guest_token;
    },

    waitForSdk(timeout = 5000) {
      return new Promise((resolve, reject) => {
        // Already loaded
        if (window.supersetEmbeddedSdk?.embedDashboard) {
          return resolve(window.supersetEmbeddedSdk);
        }
        const interval = 100;
        let elapsed = 0;
        const timer = setInterval(() => {
          if (window.supersetEmbeddedSdk?.embedDashboard) {
            clearInterval(timer);
            resolve(window.supersetEmbeddedSdk);
          } else if (elapsed >= timeout) {
            clearInterval(timer);
            reject(
              new Error(
                "Superset SDK failed to load within timeout. Check the CDN script tag in index.html.",
              ),
            );
          }
          elapsed += interval;
        }, interval);
      });
    },

    async initEmbed() {
      this.loading = true;
      this.error = null;
      const sdk = window.supersetEmbeddedSdk;
      const guestToken = await this.fetchGuestToken();
      try {
        const dashboard = await sdk.embedDashboard({
          id: this.EMBEDDED_ID,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint: this.$refs.mount,
          fetchGuestToken: () => Promise.resolve(guestToken),
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: false,
            filters: {
              expanded: true,
            },
            standalone: 2,
            themeMode: "light",
          },
        });
        // this.$nextTick(() => {
        //   const iframe = this.$refs.mount.querySelector('iframe')
        //   if (iframe && iframe.src) {
        //     const url = new URL(iframe.src)
        //     // url.searchParams.set('standalone', '3')
        //     url.searchParams.set('theme', 'light')
        //     iframe.src = url.toString()
        //   }
        // });
        // this.injectTheme();
        // this.forceIframeLightMode();
      } catch (err) {
        console.error("Superset embed error:", err);
        this.error = `Dashboard failed to load: ${err.message}`;
      } finally {
        this.loading = false;
      }
    },
    forceIframeLightMode() {
      const iframe = this.$refs.mount?.querySelector("iframe");
      if (!iframe) return;

      iframe.addEventListener("load", () => {
        // Set color-scheme on the iframe's html element if same-origin
        try {
          const doc = iframe.contentDocument;
          if (doc) {
            doc.documentElement.style.colorScheme = "light";
            doc.documentElement.setAttribute("data-theme", "light");
          }
        } catch (e) {
          // Cross-origin: can't access contentDocument — rely on server-side config
          console.warn("Cross-origin iframe, theme must be forced server-side");
        }
      });
    },
    injectTheme() {
      // Wait for iframe to load, then inject CSS
      const iframe = this.$refs.mount.querySelector("iframe");
      if (!iframe) return;

      iframe.addEventListener("load", () => {
        // Option A: Force dark/light via URL param (append to iframe src)
        // Superset respects ?theme=dark in some versions

        // Option B: postMessage to iframe (if same origin)
        iframe.contentWindow?.postMessage(
          { type: "SET_THEME", theme: "light" },
          SUPERSET_DOMAIN,
        );
      });
    },
  },
};
</script>

<style scoped>
.superset-wrapper {
  position: relative;
  width: 100%;
  height: 100vh; /* adjust to fit your layout */
}
.superset-mount {
  width: 100%;
  height: 100%;
}
/* The SDK injects an iframe — make it fill the container */
.superset-mount :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
}
.superset-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  color: #666;
}
.superset-error {
  color: #c0392b;
  padding: 16px;
  font-weight: 500;
}
.superset-wrapper iframe {
  color-scheme: light;
}
</style>
